import { useEffect, useRef, useState } from "react";
import { Mic, Square, Loader2 } from "lucide-react";
import { api } from "../lib/api";
import { toast } from "sonner";

/**
 * Textarea with built-in voice input. When the mic button is tapped, audio is
 * recorded and transcribed via the backend STT endpoint; the result is
 * APPENDED to the existing text so users can mix typing + dictation.
 *
 * Props:
 *  - value, onChange: controlled textarea state
 *  - placeholder, rows, disabled
 *  - testIdPrefix: namespace for data-testid attrs
 */
const VoiceTextInput = ({
  value,
  onChange,
  placeholder = "Schreiben oder Mikrofon antippen…",
  rows = 4,
  disabled = false,
  testIdPrefix = "voice-input",
  maxLength = 4000,
}) => {
  const [recording, setRecording] = useState(false);
  const [processing, setProcessing] = useState(false);
  const mediaRecorderRef = useRef(null);
  const streamRef = useRef(null);
  const chunksRef = useRef([]);
  const cancelledRef = useRef(false);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (streamRef.current) streamRef.current.getTracks().forEach((t) => t.stop());
      const mr = mediaRecorderRef.current;
      if (mr && mr.state !== "inactive") {
        try { mr.stop(); } catch (err) { console.warn("unmount: recorder stop", err); }
      }
    };
  }, []);

  const startRecording = async () => {
    if (recording || processing || disabled) return;
    cancelledRef.current = false;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
          channelCount: 1,
          sampleRate: 16000,
        },
      });
      streamRef.current = stream;
      const mimeCandidates = ["audio/webm;codecs=opus", "audio/webm", "audio/mp4", "audio/ogg"];
      const mimeType = mimeCandidates.find((m) => MediaRecorder.isTypeSupported(m)) || "";
      const opts = { audioBitsPerSecond: 24000 };
      if (mimeType) opts.mimeType = mimeType;
      const mr = new MediaRecorder(stream, opts);
      chunksRef.current = [];
      mr.ondataavailable = (e) => { if (e.data && e.data.size > 0) chunksRef.current.push(e.data); };
      mr.onstop = async () => {
        if (streamRef.current) {
          streamRef.current.getTracks().forEach((t) => t.stop());
          streamRef.current = null;
        }
        setRecording(false);
        if (cancelledRef.current) return;

        const usedMime = mr.mimeType || "audio/webm";
        const blob = new Blob(chunksRef.current, { type: usedMime });
        if (blob.size < 2500) {
          toast.error("Aufnahme zu kurz", { description: "Bitte mindestens 1 Sekunde sprechen." });
          return;
        }
        setProcessing(true);
        const ext = usedMime.includes("mp4") ? "mp4" : usedMime.includes("ogg") ? "ogg" : "webm";
        const form = new FormData();
        form.append("audio", blob, `voice.${ext}`);
        try {
          const { data } = await api.post("/voice/transcribe", form, {
            headers: { "Content-Type": "multipart/form-data" },
          });
          const transcript = (data?.text || "").trim();
          if (transcript) {
            // Append to existing text with smart spacing.
            const prev = value || "";
            const sep = prev && !prev.endsWith(" ") && !prev.endsWith("\n") ? " " : "";
            const next = (prev + sep + transcript).slice(0, maxLength);
            onChange(next);
          } else {
            toast.error("Keine Sprache erkannt");
          }
        } catch (e) {
          const detail = e?.response?.data?.detail || "Transkription fehlgeschlagen";
          toast.error(detail);
        } finally {
          setProcessing(false);
        }
      };
      mediaRecorderRef.current = mr;
      mr.start();
      setRecording(true);
    } catch (e) {
      toast.error("Mikrofon-Zugriff verweigert", {
        description: "Bitte erlaube den Mikrofon-Zugriff in den Browser-Einstellungen.",
      });
    }
  };

  const stopRecording = () => {
    const mr = mediaRecorderRef.current;
    if (mr && mr.state !== "inactive") {
      try { mr.stop(); } catch (err) { console.warn("stop recorder", err); }
    } else {
      setRecording(false);
    }
  };

  const toggle = () => {
    if (recording) stopRecording();
    else startRecording();
  };

  return (
    <div className="relative">
      <textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        rows={rows}
        disabled={disabled || processing}
        maxLength={maxLength}
        className="nv-input w-full resize-none pr-14"
        data-testid={`${testIdPrefix}-textarea`}
      />
      <button
        type="button"
        onClick={toggle}
        disabled={disabled || processing}
        title={recording ? "Aufnahme stoppen" : "Spracheingabe starten"}
        aria-pressed={recording}
        className={`absolute right-2 bottom-2 w-10 h-10 rounded-full flex items-center justify-center transition-colors ${
          recording
            ? "bg-red-500 hover:bg-red-600 text-white animate-pulse"
            : processing
            ? "bg-novatus-card-2 text-novatus-text-3"
            : "bg-novatus-green hover:bg-[#25E058] text-black"
        }`}
        data-testid={`${testIdPrefix}-mic-btn`}
      >
        {processing ? <Loader2 className="w-4 h-4 animate-spin" /> :
          recording ? <Square className="w-4 h-4" /> :
          <Mic className="w-4 h-4" />}
      </button>
      {(recording || processing) && (
        <p className="text-[11px] text-novatus-text-3 mt-1.5" data-testid={`${testIdPrefix}-status`}>
          {recording ? "● Aufnahme läuft — antippen zum Stoppen" : "Transkribiere …"}
        </p>
      )}
    </div>
  );
};

export default VoiceTextInput;
